import _ from 'lodash';

import { PurposeOfChattel } from 'constants/loanApplication';
import { LoanProgramFieldName, PrepaymentPenaltyFeatureType } from 'constants/programs';
import { getConditionalObject } from 'utils/data';
import { getFormOptionValue } from 'utils/form';
import { getDownPaymentAmount, getDownPaymentPercent } from 'utils/loanApplication';

import { DOWN_PAYMENT_SYNC_SOURCE, TOTAL_LOAN_AMOUNT_FEES_KEY } from './LoanInfo.constants';

const PERCENTAGE = 100;

export const getTotalLoanAmount = ({ loanAmount, loanFees, feesKey = 'fees' }) => {
  const totalFeesAmount = _.sumBy(
    _.get(loanFees, feesKey),
    ({ financedAmount, includeIntoTotalLoanAmount, isDeleted }) =>
      !isDeleted && includeIntoTotalLoanAmount ? financedAmount : 0,
  );

  return Number(loanAmount) + totalFeesAmount;
};

// LEN-127: the part of a chattel loan the sales contract prices -- sales price + sales tax + MHIT. The base
// loan amount is this less the down payment. Null without a sales price (a hand-keyed chattel loan) and on a
// chattel refinance, which has no retail sales contract.
export const getChattelContractAmount = chattelDetails => {
  const salesPrice = Number(chattelDetails?.salesPrice ?? 0);

  if (!(salesPrice > 0) || chattelDetails?.purposeOfChattel === PurposeOfChattel.REFINANCE) {
    return null;
  }

  return salesPrice + Number(chattelDetails?.salesTax ?? 0) + Number(chattelDetails?.mhit ?? 0);
};

// LEN-205: a chattel refinance is rate/term only. Its base loan amount is the payoff of the home lien linked to the
// subject property, and it finances the same fees and prepaids a sales contract does on top of it.
export const checkIsChattelRefinance = chattelDetails =>
  chattelDetails?.purposeOfChattel === PurposeOfChattel.REFINANCE;

// The fees and prepaids the backend financed on top of the base loan amount; they do not move with it.
export const getChattelFinancedCosts = ({ baseLoanAmount, totalLoanAmount }) =>
  _.round(Math.max(Number(totalLoanAmount ?? 0) - Number(baseLoanAmount ?? 0), 0), 2);

// The price the chattel down payment is paid against, and so the basis of its %: the contract plus the costs
// financed on top of it. It does not move with the down payment.
export const getChattelTotalSalesPrice = ({ chattelDetails, baseLoanAmount, totalLoanAmount }) => {
  const contractAmount = getChattelContractAmount(chattelDetails);

  return _.isNil(contractAmount)
    ? null
    : contractAmount + getChattelFinancedCosts({ baseLoanAmount, totalLoanAmount });
};

// The loan-info response carries no down payment, so -- like a purchase's price - loan amount -- a chattel
// down payment is read back off the contract. Null without a sales price or when nothing is left for it.
export const getChattelDownPaymentFromContract = ({ chattelDetails, baseLoanAmount }) => {
  const contractAmount = getChattelContractAmount(chattelDetails);

  if (_.isNil(contractAmount) || _.isNil(baseLoanAmount)) {
    return null;
  }

  const downPayment = _.round(contractAmount - Number(baseLoanAmount), 2);

  return downPayment > 0 ? downPayment : null;
};

const getChattelSourceDownPaymentValue = ({
  source,
  contractAmount,
  totalSalesPrice,
  downPaymentValue,
  downPaymentPercent,
  baseLoanAmount,
}) => {
  if (source === DOWN_PAYMENT_SYNC_SOURCE.PERCENT) {
    return _.isNil(downPaymentPercent)
      ? null
      : _.round((Number(downPaymentPercent) / PERCENTAGE) * totalSalesPrice, 2);
  }

  if (source === DOWN_PAYMENT_SYNC_SOURCE.LOAN) {
    return _.isNil(baseLoanAmount) ? null : _.round(contractAmount - Number(baseLoanAmount), 2);
  }

  return _.isNil(downPaymentValue) ? null : Number(downPaymentValue);
};

// Keeps a chattel loan's three figures consistent the way a purchase's are: base loan amount = contract - down
// payment, whichever of $, % or base the user drove, with the % taken on the Total Sales Price. The backend
// derives the same base from the stored down payment, so the two never disagree.
export const resolveChattelDownPaymentSync = ({
  source,
  contractAmount,
  totalSalesPrice,
  downPaymentValue,
  downPaymentPercent,
  baseLoanAmount,
}) => {
  const sourceDownPayment = getChattelSourceDownPaymentValue({
    source,
    contractAmount,
    totalSalesPrice,
    downPaymentValue,
    downPaymentPercent,
    baseLoanAmount,
  });

  if (_.isNil(sourceDownPayment) || _.isNaN(sourceDownPayment)) {
    return {
      downPaymentValue: null,
      downPaymentPercent: null,
      baseLoanAmount: baseLoanAmount ?? null,
    };
  }

  const nextDownPaymentValue = _.clamp(sourceDownPayment, 0, contractAmount);

  return {
    downPaymentValue: nextDownPaymentValue,
    downPaymentPercent: getDownPaymentPercent({
      amount: nextDownPaymentValue,
      purchasePrice: totalSalesPrice,
    }),
    baseLoanAmount: _.round(contractAmount - nextDownPaymentValue, 2),
  };
};

// Bounds a down-payment $ amount to [0, purchasePrice] so the % it derives to can never leave
// [0, 100] -- e.g. a nonsensical $ entry, or a %>100 entry converted past the purchase price.
export const clampDownPaymentValue = ({ downPaymentValue, purchasePrice }) => {
  const numericPurchasePrice = Number(purchasePrice);
  const numericDownPaymentValue = _.isNil(downPaymentValue) ? 0 : Number(downPaymentValue);

  if (!numericPurchasePrice || _.isNaN(numericDownPaymentValue)) {
    return 0;
  }

  return _.clamp(numericDownPaymentValue, 0, numericPurchasePrice);
};

// HZ-3922: on a LOAN the backend derives DownPaymentValue = purchasePrice - loanAmount (the loan
// projection recomputes it), so it can't hold an independent value -- the three quantities satisfy
// purchasePrice = downPayment + loanAmount. Editing down payment ($ or %) therefore moves Loan
// Amount (purchase price fixed) so the change persists via loanAmount and reloads as the same
// down payment; editing Loan Amount re-derives down payment + %. `source` names the field the user
// drove; the resolver returns the consistent { $, %, loan } triple off the fixed Purchase Price.
// Resolves the un-clamped down payment $ implied by whichever field the user drove.
const getSourceDownPaymentValue = ({
  source,
  purchasePrice,
  downPaymentValue,
  downPaymentPercent,
  baseLoanAmount,
}) => {
  if (source === DOWN_PAYMENT_SYNC_SOURCE.PERCENT) {
    return getDownPaymentAmount({ percent: downPaymentPercent, purchasePrice });
  }

  if (source === DOWN_PAYMENT_SYNC_SOURCE.LOAN) {
    return purchasePrice - Number(baseLoanAmount);
  }

  return downPaymentValue;
};

export const resolvePurchaseDownPaymentSync = ({
  source,
  purchasePrice,
  downPaymentValue,
  downPaymentPercent,
  baseLoanAmount,
}) => {
  const price = Number(purchasePrice);

  if (!price) {
    // No usable purchase price yet (e.g. chartLegend not loaded, or a loan with no pricing) --
    // can't derive % or couple to Loan Amount. Preserve the loaded/entered $ and loan as-is.
    return {
      downPaymentValue: downPaymentValue ?? null,
      downPaymentPercent: null,
      baseLoanAmount: baseLoanAmount ?? null,
    };
  }

  const nextDownPaymentValue = clampDownPaymentValue({
    downPaymentValue: getSourceDownPaymentValue({
      source,
      purchasePrice: price,
      downPaymentValue,
      downPaymentPercent,
      baseLoanAmount,
    }),
    purchasePrice: price,
  });

  return {
    downPaymentValue: nextDownPaymentValue,
    downPaymentPercent: getDownPaymentPercent({
      amount: nextDownPaymentValue,
      purchasePrice: price,
    }),
    baseLoanAmount: price - nextDownPaymentValue,
  };
};

// The field the user drove, from the sync effect's change flags -- a %/$ edit moves Loan Amount,
// anything else (a Loan Amount edit, or mount) re-derives down payment + % off the loan.
// A field cleared or unmounted reads as null or undefined; either way it holds no value, so that is no edit.
export const hasFieldChanged = (previous, current) =>
  !(_.isNil(previous) && _.isNil(current)) && previous !== current;

export const getDownPaymentSyncSource = ({
  hasDownPaymentPercentChanged,
  hasDownPaymentValueChanged,
}) => {
  if (hasDownPaymentPercentChanged) {
    return DOWN_PAYMENT_SYNC_SOURCE.PERCENT;
  }

  if (hasDownPaymentValueChanged) {
    return DOWN_PAYMENT_SYNC_SOURCE.DOWN_PAYMENT;
  }

  return DOWN_PAYMENT_SYNC_SOURCE.LOAN;
};

const rebasePendingChange = (pendingChange, initialValue) =>
  _.isNil(pendingChange?.newValue) ? pendingChange : { ...pendingChange, initialValue };

export const rebaseDerivedLoanInfoPendingChanges = ({
  formPendingChanges,
  isPurchase,
  purchasePrice,
  loanFees,
  chattelContractAmount = null,
  isChattelRefinance = false,
}) => {
  const initialLoanAmount = formPendingChanges?.baseLoanAmount?.initialValue;

  // A chattel refinance has no down payment, and its Total Loan Amount is the backend's (with financed prepaids).
  if (_.isNil(initialLoanAmount) || isChattelRefinance) {
    return formPendingChanges;
  }

  const { downPaymentValue, totalLoanAmount } = formPendingChanges;

  // LEN-127: a chattel sales contract's down payment is read back as contract - base, and its Total Loan
  // Amount is the backend's (with financed prepaids), so the loaded values are the baseline, not fee sums.
  if (!_.isNil(chattelContractAmount)) {
    return {
      ...formPendingChanges,
      ...getConditionalObject(!!downPaymentValue, () => ({
        downPaymentValue: rebasePendingChange(
          downPaymentValue,
          _.round(Math.max(chattelContractAmount - Number(initialLoanAmount), 0), 2),
        ),
      })),
    };
  }

  const shouldRebaseDownPayment = isPurchase && !!Number(purchasePrice) && !!downPaymentValue;
  const shouldRebaseTotalLoanAmount = !!loanFees && !!totalLoanAmount;

  return {
    ...formPendingChanges,
    ...getConditionalObject(shouldRebaseDownPayment, () => ({
      downPaymentValue: rebasePendingChange(
        downPaymentValue,
        resolvePurchaseDownPaymentSync({
          source: DOWN_PAYMENT_SYNC_SOURCE.LOAN,
          purchasePrice,
          baseLoanAmount: initialLoanAmount,
        }).downPaymentValue,
      ),
    })),
    ...getConditionalObject(shouldRebaseTotalLoanAmount, () => ({
      // Total Loan Amount is never typed; the form re-derives it, so it is not the user's change.
      totalLoanAmount: {
        ...rebasePendingChange(
          totalLoanAmount,
          getTotalLoanAmount({
            loanAmount: initialLoanAmount,
            loanFees,
            feesKey: TOTAL_LOAN_AMOUNT_FEES_KEY,
          }),
        ),
        isDerivedInForm: true,
      },
    })),
  };
};

/**
 * The borrower's prepayment penalty choice only prices a program whose penalty is optional; a
 * choice already stored stays visible so it is never kept without being shown.
 */
export const checkIsPrepaymentPenaltyChoiceVisible = ({ form, applicationData }) => {
  const isOptionalPrepaymentPenalty =
    !!form[LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY] &&
    getFormOptionValue(form[LoanProgramFieldName.PREPAY_FEATURE_TYPE]) ===
      PrepaymentPenaltyFeatureType.OPTIONAL;

  return (
    isOptionalPrepaymentPenalty ||
    !_.isNil(applicationData?.prepaymentPenaltyTermMonths) ||
    !_.isNil(applicationData?.prepaymentPenaltyStructure)
  );
};
